import Phaser from 'phaser';
import { GAME_CONFIG } from '../config/game';
import { getHighestLevel } from '../systems/SaveState';
import { getSoundSystem } from '../systems/SoundSystem';
import { getMusicPlayer } from '../systems/MusicPlayer';
import { getLevelStars, getLevelTimeStars } from '../systems/StarRating';
import { BackHandler, pushSceneState } from '../systems/BackButton';

const NODE_RADIUS = 24;
const NODE_SPACING_Y = 120;
const PATH_AMPLITUDE = 80;
const BIOME_LEVELS = 5;
const LOCKED_PREVIEW_COUNT = 3;
const RENDER_AHEAD_COUNT = 15;
const SCROLL_PADDING_TOP = 200;
const SCROLL_PADDING_BOTTOM = 300;

export class WorldMapScene extends Phaser.Scene implements BackHandler {
  private scrollContainer!: Phaser.GameObjects.Container;
  private detailPopup: Phaser.GameObjects.Container | null = null;
  private popupJustOpened = false;
  private returnTo: string | null = null;
  private highestLevel = 1;
  private totalHeight = 0;
  private scrollY = 0;
  private maxScrollY = 0;
  private dragStartY = 0;
  private isDragging = false;
  private velocity = 0;
  private lastPointerY = 0;
  private lastPointerTime = 0;

  constructor() {
    super('WorldMapScene');
  }

  handleBack(): void {
    if (this.returnTo === 'GameScene') {
      getMusicPlayer().stop();
      this.scene.stop();
      this.scene.wake('GameScene');
    } else {
      if (this.scene.isSleeping('GameScene')) {
        this.scene.stop('GameScene');
      }
      this.scene.start('TitleScene');
    }
  }

  init(data: { returnTo?: string }): void {
    this.returnTo = data?.returnTo ?? null;
  }

  preload(): void {
    const grounds = ['ground-wetland', 'ground-forest', 'ground-alpine', 'ground-coral', 'ground-meadow', 'ground-degraded'];
    for (const key of grounds) {
      if (!this.textures.exists(key)) {
        this.load.image(key, `assets/tiles/${key}.png`);
      }
    }
  }

  create(): void {
    pushSceneState();
    getMusicPlayer().startMenu();
    this.highestLevel = getHighestLevel();

    this.scrollContainer = this.add.container(0, 0);

    const lastScrollableLevel = this.highestLevel + LOCKED_PREVIEW_COUNT;
    const lastRenderedLevel = this.highestLevel + RENDER_AHEAD_COUNT;
    this.totalHeight = SCROLL_PADDING_TOP + lastRenderedLevel * NODE_SPACING_Y + SCROLL_PADDING_BOTTOM;
    this.maxScrollY = this.levelToY(lastScrollableLevel) - GAME_CONFIG.height * 0.3;

    this.drawBiomeBackgrounds(lastRenderedLevel);
    this.drawPath(lastRenderedLevel);
    this.drawNodes(lastRenderedLevel);
    this.drawCloseButton();

    this.scrollToLevel(this.highestLevel, false);
    this.setupInput();
  }

  update(_time: number, delta: number): void {
    if (!this.isDragging && Math.abs(this.velocity) > 0.5) {
      this.scrollY += this.velocity * (delta / 16);
      this.velocity *= 0.92;
      this.clampScroll();
      this.scrollContainer.y = -this.scrollY;
    }
  }

  private drawBiomeBackgrounds(lastLevel: number): void {
    const width = GAME_CONFIG.width;
    const biomeCount = Math.ceil(lastLevel / BIOME_LEVELS);
    const tileSize = 128;
    const trailExtension = NODE_SPACING_Y * 6;
    const fadeZone = 30;
    const whiteGap = 5;
    const groundKeys = ['ground-wetland', 'ground-forest', 'ground-alpine', 'ground-coral', 'ground-meadow'];
    const biomeColors = [0x2e8b8b, 0x2e7d32, 0xb8d4e3, 0xe07a5f, 0xdda15e];
    const degradedColor = 0x3d3522;

    const lastCompletedLevel = this.highestLevel - 1;
    const lastRestoredBiome = Math.floor((lastCompletedLevel - 1) / BIOME_LEVELS);

    // Draw tiles in reverse order so lower biomes render on top at boundaries
    for (let b = biomeCount - 1; b >= 0; b--) {
      const biomeStartLevel = b * BIOME_LEVELS + 1;
      const biomeEndLevel = biomeStartLevel + BIOME_LEVELS - 1;
      const bottomY = this.levelToY(biomeStartLevel) + NODE_SPACING_Y / 2;
      let topY = this.levelToY(biomeEndLevel) - NODE_SPACING_Y / 2;

      if (b === biomeCount - 1) {
        topY -= trailExtension;
      }

      const height = bottomY - topY;
      const isRestored = biomeEndLevel <= lastCompletedLevel;
      const isPartiallyRestored = !isRestored && biomeStartLevel <= lastCompletedLevel;
      const groundKey = isRestored ? groundKeys[b % 5] : 'ground-degraded';
      const cols = Math.ceil(width / tileSize);
      const rows = Math.ceil(height / tileSize);

      const bgRect = this.add.rectangle(width / 2, topY + height / 2, width, height, 0x1a1a2e, 1);
      this.scrollContainer.add(bgRect);

      if (isPartiallyRestored) {
        const splitY = this.levelToY(lastCompletedLevel) - NODE_SPACING_Y / 2;

        // Degraded portion first (above splitY = lower Y values)
        const degradedRows = Math.ceil((splitY - topY) / tileSize);
        for (let row = 0; row < degradedRows; row++) {
          for (let col = 0; col < cols; col++) {
            const tileY = topY + row * tileSize + tileSize / 2;
            const tile = this.add.image(col * tileSize + tileSize / 2, tileY, 'ground-degraded');
            tile.setAlpha(0.5);
            this.scrollContainer.add(tile);
          }
        }

        // Restored portion on top (below splitY = higher Y values)
        const restoredHeight = bottomY - splitY;
        const restoredBg = this.add.rectangle(width / 2, splitY + restoredHeight / 2, width, restoredHeight, 0x1a1a2e, 1);
        this.scrollContainer.add(restoredBg);

        const restoredRows = Math.ceil(restoredHeight / tileSize);
        for (let row = 0; row < restoredRows; row++) {
          for (let col = 0; col < cols; col++) {
            const tileY = splitY + row * tileSize + tileSize / 2;
            const tile = this.add.image(col * tileSize + tileSize / 2, tileY, groundKeys[b % 5]);
            tile.setAlpha(0.5);
            this.scrollContainer.add(tile);
          }
        }
      } else {
        for (let row = 0; row < rows; row++) {
          for (let col = 0; col < cols; col++) {
            const tileY = topY + row * tileSize + tileSize / 2;
            const tile = this.add.image(col * tileSize + tileSize / 2, tileY, groundKey);
            tile.setAlpha(0.5);
            this.scrollContainer.add(tile);
          }
        }
      }
    }

    // Draw biome-to-biome fade transitions (only within restored zone)
    for (let b = 1; b < biomeCount; b++) {
      const biomeStartLevel = b * BIOME_LEVELS + 1;
      if (biomeStartLevel > lastCompletedLevel + 1) break;

      const boundaryY = this.levelToY(biomeStartLevel) + NODE_SPACING_Y / 2;
      const steps = 10;
      const stepHeight = fadeZone / steps;

      const lowerColor = biomeColors[(b - 1) % 5];
      const upperColor = biomeStartLevel <= lastCompletedLevel
        ? biomeColors[b % 5]
        : degradedColor;
      const midColor = this.averageColors(lowerColor, upperColor);

      for (let i = 0; i < steps; i++) {
        const y = boundaryY + whiteGap / 2 + i * stepHeight;
        const alpha = 1 - (i / steps);
        const rect = this.add.rectangle(width / 2, y + stepHeight / 2, width, stepHeight, midColor, alpha);
        this.scrollContainer.add(rect);
      }

      const band = this.add.rectangle(width / 2, boundaryY, width, whiteGap, midColor, 1);
      this.scrollContainer.add(band);

      for (let i = 0; i < steps; i++) {
        const y = boundaryY - whiteGap / 2 - (i + 1) * stepHeight;
        const alpha = 1 - (i / steps);
        const rect = this.add.rectangle(width / 2, y + stepHeight / 2, width, stepHeight, midColor, alpha);
        this.scrollContainer.add(rect);
      }
    }

    // Draw restoration boundary fade (between last restored level and degraded)
    if (lastCompletedLevel >= 1 && lastCompletedLevel % BIOME_LEVELS !== 0) {
      const boundaryY = this.levelToY(lastCompletedLevel) - NODE_SPACING_Y / 2;
      const steps = 10;
      const stepHeight = fadeZone / steps;

      const restoredBiomeColor = biomeColors[lastRestoredBiome % 5];
      const midColor = this.averageColors(restoredBiomeColor, degradedColor);

      for (let i = 0; i < steps; i++) {
        const y = boundaryY + whiteGap / 2 + i * stepHeight;
        const alpha = 1 - (i / steps);
        const rect = this.add.rectangle(width / 2, y + stepHeight / 2, width, stepHeight, midColor, alpha);
        this.scrollContainer.add(rect);
      }

      const band = this.add.rectangle(width / 2, boundaryY, width, whiteGap, midColor, 1);
      this.scrollContainer.add(band);

      for (let i = 0; i < steps; i++) {
        const y = boundaryY - whiteGap / 2 - (i + 1) * stepHeight;
        const alpha = 1 - (i / steps);
        const rect = this.add.rectangle(width / 2, y + stepHeight / 2, width, stepHeight, midColor, alpha);
        this.scrollContainer.add(rect);
      }
    }
  }

  private drawPath(lastLevel: number): void {
    const graphics = this.add.graphics();
    graphics.lineStyle(3, 0xffffff, 0.3);
    graphics.beginPath();

    const trailLevels = lastLevel + 5;
    for (let level = 1; level <= trailLevels; level++) {
      const pos = this.levelToPosition(level);
      if (level === 1) {
        graphics.moveTo(pos.x, pos.y);
      } else {
        graphics.lineTo(pos.x, pos.y);
      }
    }

    graphics.strokePath();
    this.scrollContainer.add(graphics);
  }

  private drawNodes(lastLevel: number): void {
    for (let level = 1; level <= lastLevel; level++) {
      const pos = this.levelToPosition(level);
      const state = this.getLevelState(level);
      this.createNode(level, pos.x, pos.y, state);
    }
  }

  private createNode(level: number, x: number, y: number, state: 'completed' | 'current' | 'locked'): void {
    const container = this.add.container(x, y);
    this.scrollContainer.add(container);

    if (state === 'locked') {
      const circle = this.add.circle(0, 0, NODE_RADIUS, 0x333333, 0.6);
      container.add(circle);

      const lockText = this.add.text(0, 0, '🔒', {
        fontSize: '18px',
        fontFamily: 'sans-serif',
      }).setOrigin(0.5);
      container.add(lockText);
      return;
    }

    const biomeColors: number[] = [0x2e8b8b, 0x2e7d32, 0xb8d4e3, 0xe07a5f, 0xdda15e];
    const biomeIndex = Math.floor((level - 1) / BIOME_LEVELS) % 5;
    const fill = this.add.circle(0, 0, NODE_RADIUS, biomeColors[biomeIndex]);
    container.add(fill);

    const borderColor = state === 'current' ? 0xa8e6cf : 0xffffff;
    const border = this.add.circle(0, 0, NODE_RADIUS);
    border.setStrokeStyle(3, borderColor);
    border.setFillStyle(0x000000, 0);
    container.add(border);

    const levelText = this.add.text(0, 0, String(level), {
      fontSize: '14px',
      color: '#ffffff',
      fontFamily: 'sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 3,
    }).setOrigin(0.5);
    container.add(levelText);

    if (state === 'current') {
      this.tweens.add({
        targets: border,
        scaleX: 1.15,
        scaleY: 1.15,
        duration: 800,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }

    if (state === 'completed') {
      const starData = getLevelStars(level);
      const earned = starData?.stars ?? 0;
      const starStr = '★'.repeat(earned) + '☆'.repeat(3 - earned);
      const starsText = this.add.text(0, -NODE_RADIUS - 14, starStr, {
        fontSize: '12px',
        color: '#ffd700',
        fontFamily: 'sans-serif',
        stroke: '#000000',
        strokeThickness: 2,
      }).setOrigin(0.5);
      container.add(starsText);

      const timeData = getLevelTimeStars(level);
      if (timeData) {
        const timeEarned = timeData.stars;
        const timeStr = '★'.repeat(timeEarned) + '☆'.repeat(3 - timeEarned);
        const timeStarsText = this.add.text(0, -NODE_RADIUS - 28, timeStr, {
          fontSize: '12px',
          color: '#7ec8e3',
          fontFamily: 'sans-serif',
          stroke: '#000000',
          strokeThickness: 2,
        }).setOrigin(0.5);
        container.add(timeStarsText);
      }
    }

    const hitZone = this.add.circle(0, 0, NODE_RADIUS + 8, 0xffffff, 0);
    hitZone.setInteractive();
    container.add(hitZone);

    hitZone.on('pointerdown', () => {
      if (this.isDragging) return;
      getSoundSystem().playBlip();
      if (state === 'completed') {
        this.showLevelDetail(level, x, y);
      } else {
        if (this.scene.isSleeping('GameScene')) {
          this.scene.stop('GameScene');
        }
        this.scene.start('GameScene', { level, replay: false });
      }
    });
  }

  private drawCloseButton(): void {
    const closeBtn = this.add.text(GAME_CONFIG.width - 28, 30, '✕', {
      fontSize: '28px',
      color: '#ffffff',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5).setInteractive();

    closeBtn.on('pointerdown', () => {
      getSoundSystem().playBlip();
      if (this.returnTo === 'GameScene') {
        getMusicPlayer().stop();
        this.scene.stop();
        this.scene.wake('GameScene');
      } else {
        if (this.scene.isSleeping('GameScene')) {
          this.scene.stop('GameScene');
        }
        this.scene.start('TitleScene');
      }
    });
  }

  private setupInput(): void {
    let pointerDown = false;
    let startScrollY = 0;
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      pointerDown = true;
      this.isDragging = false;
      this.velocity = 0;
      this.dragStartY = pointer.y;
      startScrollY = this.scrollY;
      this.lastPointerY = pointer.y;
      this.lastPointerTime = pointer.time;

      if (this.detailPopup && !this.popupJustOpened) {
        this.detailPopup.destroy();
        this.detailPopup = null;
      }
      this.popupJustOpened = false;
    });

    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (!pointerDown) return;
      const dy = pointer.y - this.dragStartY;
      if (Math.abs(dy) > 8) {
        this.isDragging = true;
        if (this.detailPopup) {
          this.detailPopup.destroy();
          this.detailPopup = null;
        }
      }
      if (this.isDragging) {
        this.scrollY = startScrollY - dy;
        this.clampScroll();
        this.scrollContainer.y = -this.scrollY;
        this.lastPointerY = pointer.y;
        this.lastPointerTime = pointer.time;
      }
    });

    this.input.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      if (this.isDragging) {
        const dt = pointer.time - this.lastPointerTime;
        if (dt > 0 && dt < 100) {
          this.velocity = (this.lastPointerY - pointer.y) / (dt / 16);
        }
      }
      pointerDown = false;
      this.time.delayedCall(50, () => {
        this.isDragging = false;
      });
    });
  }

  private scrollToLevel(level: number, animate: boolean): void {
    const targetY = this.levelToY(level) - GAME_CONFIG.height / 2;
    this.scrollY = targetY;
    this.clampScroll();

    if (animate) {
      this.tweens.add({
        targets: this.scrollContainer,
        y: -this.scrollY,
        duration: 500,
        ease: 'Sine.easeOut',
      });
    } else {
      this.scrollContainer.y = -this.scrollY;
    }
  }

  private clampScroll(): void {
    const minScroll = this.maxScrollY;
    const maxScroll = this.totalHeight - GAME_CONFIG.height;
    this.scrollY = Math.max(minScroll, Math.min(this.scrollY, maxScroll));
  }

  private levelToY(level: number): number {
    return this.totalHeight - SCROLL_PADDING_BOTTOM - (level - 1) * NODE_SPACING_Y;
  }

  private levelToPosition(level: number): { x: number; y: number } {
    const y = this.levelToY(level);
    const centerX = GAME_CONFIG.width / 2;
    const progress = ((level - 1) % BIOME_LEVELS) / (BIOME_LEVELS - 1);
    const x = centerX + Math.sin(progress * Math.PI * 2) * PATH_AMPLITUDE;
    return { x, y };
  }

  private getLevelState(level: number): 'completed' | 'current' | 'locked' {
    if (level < this.highestLevel) return 'completed';
    if (level === this.highestLevel) return 'current';
    return 'locked';
  }

  private showLevelDetail(level: number, nodeX: number, nodeY: number): void {
    if (this.detailPopup) {
      this.detailPopup.destroy();
      this.detailPopup = null;
    }

    const starData = getLevelStars(level);
    const stars = starData?.stars ?? 0;
    const bestScore = starData?.bestScore ?? 0;
    const timeData = getLevelTimeStars(level);
    const timeStars = timeData?.stars ?? 0;

    const popupHeight = 132;
    const popup = this.add.container(nodeX, nodeY - NODE_RADIUS - popupHeight / 2 - 10);
    this.scrollContainer.add(popup);

    const bg = this.add.rectangle(0, 0, 180, popupHeight, 0x1a1a2e, 0.95);
    bg.setStrokeStyle(2, 0xa8e6cf);
    popup.add(bg);

    let yOffset = -popupHeight / 2 + 18;

    const title = this.add.text(0, yOffset, `Level ${level}`, {
      fontSize: '14px',
      color: '#ffffff',
      fontFamily: 'sans-serif',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    popup.add(title);

    yOffset += 22;
    const starStr = '★'.repeat(stars) + '☆'.repeat(3 - stars);
    const starsText = this.add.text(0, yOffset, starStr, {
      fontSize: '16px',
      color: '#ffd700',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5);
    popup.add(starsText);

    yOffset += 18;
    const scoreText = this.add.text(0, yOffset, `Best: ${bestScore}`, {
      fontSize: '11px',
      color: '#888888',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5);
    popup.add(scoreText);

    yOffset += 18;
    const timeStarStr = '★'.repeat(timeStars) + '☆'.repeat(3 - timeStars);
    const timeLabel = timeData
      ? `⏱ ${timeStarStr}`
      : `⏱ ☆☆☆`;
    const timeText = this.add.text(0, yOffset, timeLabel, {
      fontSize: '16px',
      color: '#7ec8e3',
      fontFamily: 'sans-serif',
    }).setOrigin(0.5);
    popup.add(timeText);

    yOffset += 28;
    const playBtn = this.add.text(-40, yOffset, 'Play', {
      fontSize: '13px',
      color: '#1a1a2e',
      fontFamily: 'sans-serif',
      backgroundColor: '#a8e6cf',
      padding: { x: 12, y: 6 },
    }).setOrigin(0.5).setInteractive();
    popup.add(playBtn);

    playBtn.on('pointerdown', () => {
      getSoundSystem().playBlip();
      if (this.scene.isSleeping('GameScene')) {
        this.scene.stop('GameScene');
      }
      this.scene.start('GameScene', { level, replay: true });
    });

    const timeBtn = this.add.text(40, yOffset, '⏱ Go', {
      fontSize: '13px',
      color: '#1a1a2e',
      fontFamily: 'sans-serif',
      backgroundColor: '#7ec8e3',
      padding: { x: 12, y: 6 },
    }).setOrigin(0.5).setInteractive();
    popup.add(timeBtn);

    timeBtn.on('pointerdown', () => {
      getSoundSystem().playBlip();
      if (this.scene.isSleeping('GameScene')) {
        this.scene.stop('GameScene');
      }
      this.scene.start('GameScene', { level, timeAttack: true });
    });

    this.detailPopup = popup;
    this.popupJustOpened = true;

    this.time.delayedCall(5000, () => {
      if (this.detailPopup === popup) {
        popup.destroy();
        this.detailPopup = null;
      }
    });
  }

  private averageColors(c1: number, c2: number): number {
    const darken = 0.75;
    const r = Math.round((((c1 >> 16) & 0xff) + ((c2 >> 16) & 0xff)) / 2 * darken);
    const g = Math.round((((c1 >> 8) & 0xff) + ((c2 >> 8) & 0xff)) / 2 * darken);
    const b = Math.round(((c1 & 0xff) + (c2 & 0xff)) / 2 * darken);
    return (r << 16) | (g << 8) | b;
  }

}
